import type { AppCustomRouteRecordRaw } from '@/router/types'
import { FIRM_ERP_ROUTE_BASE } from '@/utils/firmErpRoutes'

/** 菜单 path / permission → firmModules key */
const FIRM_MENU_PATH_MODULE: Record<string, string> = {
  customer: 'customer',
  matter: 'matter',
  'contract-template': 'matter',
  dashboard: 'matter',
  org: 'matter',
  hearing: 'matter',
  task: 'matter',
  deadline: 'matter',
  alert: 'matter',
  dossier: 'dossier',
  time: 'timeBilling',
  fee: 'timeBilling',
  report: 'timeBilling',
  invoice: 'timeBilling',
  refund: 'timeBilling',
  commission: 'timeBilling',
  retainer: 'timeBilling',
  finance: 'timeBilling',
  'admin-notice': 'adminNotice',
  'admin-doc': 'adminDoc',
  'admin-calendar': 'adminCalendar',
  'admin-seal': 'adminSeal',
  integration: 'integration'
}

const DEFAULT_FIRM_MODULES: Record<string, boolean> = {
  customer: true,
  matter: true,
  timeBilling: true,
  dossier: true,
  adminNotice: true,
  adminDoc: true,
  adminCalendar: true,
  adminSeal: true,
  integration: false
}

export function normalizeFirmModules(
  modules?: Record<string, boolean> | null
): Record<string, boolean> {
  return { ...DEFAULT_FIRM_MODULES, ...(modules || {}) }
}

export function isFirmModuleEnabled(
  modules: Record<string, boolean> | undefined,
  moduleKey: string
): boolean {
  const normalized = normalizeFirmModules(modules)
  return normalized[moduleKey] !== false
}

/** 律所 ERP 顶级目录（已从律师平台拆出，path=/lawyer-firm） */
export function isFirmErpRootMenu(route: AppCustomRouteRecordRaw): boolean {
  const path = String(route.path || '')
  const name = String(route.name || route.meta?.title || '')
  if (name === '律所 ERP') return true
  if (path === 'firm' || path.endsWith('/firm')) return true
  if (path === FIRM_ERP_ROUTE_BASE || path === 'lawyer-firm' || path.endsWith('/lawyer-firm')) {
    return true
  }
  return false
}

function resolveFirmModuleKey(route: AppCustomRouteRecordRaw): string | null {
  const perm = String(route.permission || route.meta?.permission || '')
  if (perm.startsWith('lawyer:firm:')) {
    const parts = perm.split(':')
    if (parts.length >= 4 && parts[2] === 'admin') {
      const sub = parts[3]
      if (sub === 'notice') return 'adminNotice'
      if (sub === 'doc') return 'adminDoc'
      if (sub === 'calendar') return 'adminCalendar'
      if (sub === 'seal') return 'adminSeal'
    }
    if (parts.length >= 3) {
      const resource = parts[2]
      if (resource === 'contract-template' || resource === 'contract') return 'matter'
      if (resource === 'dashboard' || resource === 'org') return 'matter'
      if (resource === 'dossier') return 'dossier'
      if (resource === 'customer') return 'customer'
      if (resource === 'matter') return 'matter'
      if (resource === 'hearing' || resource === 'task' || resource === 'deadline' || resource === 'alert') {
        return 'matter'
      }
      if (
        resource === 'time' ||
        resource === 'fee-rate' ||
        resource === 'fee' ||
        resource === 'report' ||
        resource === 'invoice' ||
        resource === 'refund' ||
        resource === 'commission' ||
        resource === 'retainer' ||
        resource === 'finance'
      ) {
        return 'timeBilling'
      }
      if (resource === 'integration') return 'integration'
    }
  }

  const path = String(route.path || '')
  const segment = path.split('/').filter(Boolean).pop() || ''
  const mapped = FIRM_MENU_PATH_MODULE[segment]
  if (!mapped) return null

  // 短 path（customer）仅当组件落在 Track F 视图时才算 ERP 菜单
  const component = String((route as any).component || '')
  if (component.includes('lawyer/firm')) {
    return mapped
  }
  if (
    path.includes('/lawyer-firm') ||
    path.includes('lawyer/firm') ||
    path === 'firm' ||
    path.endsWith('/firm')
  ) {
    return mapped
  }
  return null
}

export function filterMenusByFirmModules(
  routers: AppCustomRouteRecordRaw[],
  firmModules?: Record<string, boolean> | null
): AppCustomRouteRecordRaw[] {
  if (!routers?.length) return routers
  const modules = normalizeFirmModules(firmModules)

  return routers
    .map((route): AppCustomRouteRecordRaw | null => {
      let filteredChildren: AppCustomRouteRecordRaw[] | undefined
      if (route.children?.length) {
        filteredChildren = filterMenusByFirmModules(route.children, modules)
      }

      const moduleKey = resolveFirmModuleKey(route)
      if (moduleKey && !isFirmModuleEnabled(modules, moduleKey)) {
        return null
      }

      if (
        route.children?.length &&
        (!filteredChildren || filteredChildren.length === 0) &&
        moduleKey == null
      ) {
        if (isFirmErpRootMenu(route)) {
          return null
        }
      }

      return { ...route, children: filteredChildren }
    })
    .filter((route): route is AppCustomRouteRecordRaw => route !== null)
}
